<script lang="ts">
import { defineComponent, ref, reactive, watch, computed } from '@vue/composition-api';

import { queryExecutor } from '@/_core/cqrs';
import { UserNotificationSettingsQuery, UserNotificationSettings } from '@/api/notification-settings/queries';
import {
    useNotificationSettingsStore,
    InitNotificationSettings,
    UpdateUserNotificationSettings,
} from '@/store/global/notification-settings';
import { NotificationProviderSettings } from '@/store/global/notification-settings/types';
import PlatformIcons from './PlatformIcons.vue';
import NotificationSettingsBlock from './NotificationSettingsBlock.vue';
import SettingsBlockRow from './SettingsBlockRow.vue';
import SlackConnector from './SlackConnector.vue';
import { snackbar } from '@/_core/app-components/use';

interface IProps {
    isOpen: boolean;
}

const useInitStore = async () => {
    const res = await queryExecutor.executeQuery(new UserNotificationSettingsQuery());
    const store = useNotificationSettingsStore();
    await store.dispatch(new InitNotificationSettings(res));
    return;
};

const NotificationSettings = defineComponent<IProps>({
    name: 'NotificationSettings',
    components: {
        PlatformIcons,
        NotificationSettingsBlock,
        SettingsBlockRow,
        SlackConnector,
    },
    props: { isOpen: Boolean },

    setup(props, ctx) {
        const isFullScreen = ref(false);
        const store = useNotificationSettingsStore();

        const state = reactive({
            loading: true,
            isSaving: false,
            id: computed(() => store.state.id),

            autoWatchMyCards: false,
            changeRights: {} as NotificationProviderSettings,

            firstTimeVisitedMyBoard: {} as NotificationProviderSettings,
            invitedMe: {} as NotificationProviderSettings,
            someoneCommentedOrChangedWatchedCards: {} as NotificationProviderSettings,
            someoneMentionedMe: {} as NotificationProviderSettings,
            someonePostedOnBoardDiscussions: {} as NotificationProviderSettings,
        });

        watch(
            () => props.isOpen,
            () => {
                if (props.isOpen) {
                    state.loading = true;
                    useInitStore().then(() => {
                        state.loading = false;

                        state.autoWatchMyCards = store.state.autoWatchMyCards;
                        state.changeRights = store.state.changeRights.copy();
                        state.firstTimeVisitedMyBoard = store.state.firstTimeVisitedMyBoard.copy();
                        state.invitedMe = store.state.invitedMe.copy();
                        state.someoneCommentedOrChangedWatchedCards = store.state.someoneCommentedOrChangedWatchedCards.copy();
                        state.someoneMentionedMe = store.state.someoneMentionedMe.copy();
                        state.someonePostedOnBoardDiscussions = store.state.someonePostedOnBoardDiscussions.copy();
                    });
                }
            },
            { immediate: true }
        );

        const close = () => {
            ctx.emit('close');
        };

        async function onSave() {
            state.isSaving = true;

            const settings = new UserNotificationSettings();

            settings.Id = state.id;
            settings.AutoWatchMyCards = state.autoWatchMyCards;

            settings.ChangeRights = state.changeRights.mapToDto();
            settings.FirstTimeVisitedMyBoard = state.firstTimeVisitedMyBoard.mapToDto();
            settings.InvitedMe = state.invitedMe.mapToDto();
            settings.SomeoneCommentedOrChangedWatchedCards = state.someoneCommentedOrChangedWatchedCards.mapToDto();
            settings.SomeoneMentionedMe = state.someoneMentionedMe.mapToDto();
            settings.SomeonePostedOnBoardDiscussions = state.someonePostedOnBoardDiscussions.mapToDto();

            await store.dispatch(new UpdateUserNotificationSettings(settings));

            state.isSaving = false;
            snackbar.showSuccess('Your notification settings sucessfully updated!');
        }

        return {
            close,
            onSave,

            state,
            isFullScreen,
        };
    },
});

export default NotificationSettings;
</script>

<template>
  <AppDialog
    title="Notification Settings"
    width="1084"
    :isOpen="isOpen"
    @close="close"
  >
    <!-- <focus-trap v-model="isOpen"> -->
    <v-progress-linear
      :active="state.loading"
      indeterminate
      color="primary lighten-2"
      class="mt-1"
    />

    <v-card-text style="min-height: 380px">
      <v-fade-transition mode="out-in">
        <v-skeleton-loader
          v-if="state.loading" 
          class="ma-2"
          height="300"
          type="table-cell@3, text@2"
        />
        <v-form v-else>
          <SlackConnector />

          <PlatformIcons class="mt-6" />

          <NotificationSettingsBlock title="Team activity">
            <SettingsBlockRow :model="state.invitedMe" :disabled="['inApp','email']" label="Someone invites me to a workspace or share a story map with me" />
              
            <SettingsBlockRow :model="state.changeRights" :disabled="['inApp']" label="Someone makes any changes on my workspace or story map membership and access rights" />

            <SettingsBlockRow :model="state.someoneMentionedMe" :disabled="['inApp']">
              <template slot="label">
                <div class="pt-1">
                  Someone @<strong>mentions</strong> me
                </div>
              </template>
            </SettingsBlockRow>
          </NotificationSettingsBlock>

          <NotificationSettingsBlock title="Board activity">
            <SettingsBlockRow :model="state.firstTimeVisitedMyBoard" label="Someone opens one of my boards for the first time" />
              
            <SettingsBlockRow :model="state.someoneCommentedOrChangedWatchedCards">
              <template slot="label">
                <div class="pt-1">
                  Someone comments or <span style="text-decoration: underline">makes changes</span> on my <strong>watched</strong> cards
                </div>
              </template>
            </SettingsBlockRow>
              
            <SettingsBlockRow :model="state.someonePostedOnBoardDiscussions" :disabled="['email']" label="Someone post a message in board discussions" />
          </NotificationSettingsBlock>

          <NotificationSettingsBlock title="General notification settings">
            <div class="d-flex">
              <v-checkbox
                v-model="state.autoWatchMyCards"
                class="mt-0"
                hide-details
                dense
              />
              <div class="d-flex align-center pt-1">
                Set cards I created automatically watched
              </div>
            </div>
          </NotificationSettingsBlock>

          <v-alert
            class="mt-4"
            type="info"
            dense
            text
          >
            Please note that the settings above will apply to all of your story maps.
          </v-alert>
        </v-form>
      </v-fade-transition>
    </v-card-text>
    <v-card-actions class="pt-0 px-6">
      <v-btn
        color="primary"
        depressed
        :loading="state.isSaving"
        @click="onSave"
      >
        Save
      </v-btn>
      <v-btn 
        depressed 
        color="secondaryButton"
        class="primary--text"
        @click="close"
      >
        Cancel
      </v-btn>
    </v-card-actions>
    <!-- </focus-trap> -->
  </AppDialog>
</template>
